import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLoadingView } from 'shared-ui/lib/components/auth/AuthLoadingView';
import { CardTitle } from 'shared-ui/lib/components/CardTitle';
import { GenericIndexScene } from 'shared-ui/lib/components/table/TableX';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { vehicleColumns } from '../../columns/vehicle.columns';
import { ConfirmModal } from '../../components/ConfirmModal';
import { routesConfig } from '../../config/routes.config';
import { usePartnerCustomerVehiclesLazyQuery } from '../../generated/graphql';
import { useCan } from '../../hooks/use-can';

const defaultOrderBy = { name: 'asc' as const };

const customerVehicleColumns = [
  vehicleColumns.name,
  vehicleColumns.registerNumber,
  vehicleColumns.VIN,
  vehicleColumns.license,
  vehicleColumns.model,
  vehicleColumns.brand,
  vehicleColumns.linkToVehicle,
];

export const CustomerVehiclesScene = (): JSX.Element => {
  const { t } = useTranslation('CustomerVehiclesScene');
  const navigate = useNavigate();
  const { canEditContent } = useCan();
  const { businessId } = useParams<{ businessId: string }>();
  const [fetchVehicles, queryState] = usePartnerCustomerVehiclesLazyQuery(setupQueryConfig());
  const [isAddVehicleConfirmOpen, setIsAddVehicleConfirmOpen] = useState(false);

  useEffect(() => {
    if (queryState.error) {
      navigate(routesConfig.SERVICE_CALL.CUSTOMERS);
    }
  }, [navigate, queryState.error]);

  const fetch = useCallback(
    (options: { variables?: Record<string, unknown> }) => {
      fetchVehicles({
        variables: {
          businessId,
          orderBy: options.variables?.orderBy as never,
          skip: options.variables?.skip as number | undefined,
          take: options.variables?.take as number | undefined,
        },
      });
    },
    [businessId, fetchVehicles]
  );

  const result = queryState.data?.partnerCustomerVehicles;
  const businessName = result?.businessName ?? '';
  const totalCount = result?.totalCount ?? 0;

  const handleConfirmAddVehicle = (): void => {
    setIsAddVehicleConfirmOpen(false);
    navigate(routesConfig.SERVICE_CALL.VEHICLE_CREATE.replace(':businessId', businessId));
  };

  if (queryState.error) {
    return <AuthLoadingView />;
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="p-4">
        <CardTitle>{t('title', { name: businessName })}</CardTitle>
        <div className="mb-4 flex items-center justify-between gap-4">
          <p className="text-sm text-gray-color">{t('totalCount', { count: totalCount })}</p>
          {canEditContent ? (
            <button
              type="button"
              onClick={() => setIsAddVehicleConfirmOpen(true)}
              className="inline-flex items-center justify-center py-2 px-4 border transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
            >
              <PlusIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
              {t('addVehicle')}
            </button>
          ) : undefined}
        </div>
        <GenericIndexScene
          unwrapped
          perPage={30}
          dataPath="partnerCustomerVehicles.list"
          countPath="partnerCustomerVehicles.totalCount"
          query={[fetch, queryState]}
          defaultOrderBy={defaultOrderBy}
          columns={customerVehicleColumns}
        />
        <Link
          to={routesConfig.SERVICE_CALL.CUSTOMERS}
          className="inline-flex items-center mt-4 text-sm text-primary hover:text-secondary font-semibold"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          {t('backToCustomers')}
        </Link>
      </div>
      <ConfirmModal
        isOpen={isAddVehicleConfirmOpen}
        onCancel={() => setIsAddVehicleConfirmOpen(false)}
        onConfirm={handleConfirmAddVehicle}
        title={t('addVehicleConfirmTitle', { name: businessName })}
        confirmLabel={t('addVehicleConfirm')}
        cancelLabel={t('addVehicleCancel')}
        variant="success"
      />
    </div>
  );
};
